import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../../shared/i18n/useLang";
import CasaPicker from "../components/CasaPicker";
import {
  MOTIVOS, autorizarVisitaHoy, buscarPersona, listaCasas, obtenerCasa,
  texto as textoDe, textoMotivo,
} from "../services/seguridadService";

const VACIO = {
  identificacion: "",
  nombre: "",
  placa: "",
  filial: null,
  motivo: "VISITA",
  notas: "",
};

/**
 * Autorizar una visita para hoy.
 *
 * Es lo que hace el guarda cuando un residente lo llama: deja anotado a quién
 * espera la casa, y al guardar se le manda el correo al propietario. La persona
 * todavía no entró: queda en "Autorizados de hoy" y, cuando llegue, el ingreso
 * se registra con un toque como cualquier otro.
 *
 * La autorización vale solo el día de hoy y se cierra cuando la persona marca
 * su salida; si vuelve, hay que autorizarla de nuevo.
 */
export default function GaritaAutorizar() {
  const navigate = useNavigate();
  const L = useLang();
  const [form, setForm] = useState(VACIO);
  const [casas, setCasas] = useState([]);
  const [casa, setCasa] = useState(null);           // la casa elegida, con sus correos
  const [sugerencia, setSugerencia] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [errores, setErrores] = useState({});
  const [aviso, setAviso] = useState(null);         // { tipo, texto }
  const [listo, setListo] = useState(null);         // la autorización recién creada
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    listaCasas().then(setCasas).catch(() => setCasas([]));
  }, []);

  useEffect(() => {
    if (!form.filial) { setCasa(null); return; }
    obtenerCasa(form.filial).then(setCasa).catch(() => setCasa(null));
  }, [form.filial]);

  function set(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
    setErrores((e) => ({ ...e, [campo]: undefined }));
  }

  // La cédula trae el nombre: de la casa, de una visita que ya está autorizada,
  // de alguien que ya vino antes, o del TSE / Hacienda.
  async function buscar() {
    const ident = form.identificacion.trim();
    if (ident.length < 3) return;
    setBuscando(true);
    try {
      const r = await buscarPersona({ identificacion: ident });
      setSugerencia(r.origen ? r : null);
      if (!r.origen) return;
      setForm((f) => ({
        ...f,
        nombre: f.nombre || r.nombre || "",
        placa:  f.placa || r.placa || "",
        filial: f.filial || r.filial_id || null,
        motivo: r.motivo && r.motivo !== "RESIDENTE" ? r.motivo : f.motivo,
      }));
    } catch { /* sin sugerencia: se llena a mano */ } finally {
      setBuscando(false);
    }
  }

  function validar() {
    const e = {};
    if (!form.nombre.trim()) e.nombre = L("El nombre es obligatorio.", "Name is required.");
    if (!form.filial) e.filial = L("Escogé la casa que la autoriza.", "Choose the unit authorizing it.");
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  async function guardar() {
    setAviso(null);
    if (!validar()) return;
    setGuardando(true);
    try {
      const r = await autorizarVisitaHoy(form);
      setListo(r);
      const casaAviso = r.aviso_casa || {};
      let texto = L(`${r.nombre} queda autorizado para hoy en ${r.filial}.`,
                    `${r.nombre} is authorized for today at ${r.filial}.`);
      if (casaAviso.enviado) {
        texto += L(` Se le avisó por correo a la casa (${casaAviso.correos}).`,
                   ` The unit was notified by email (${casaAviso.correos}).`);
      } else if (casaAviso.correos === 0) {
        texto += L(" Esa casa no tiene correos registrados: avisale por otro medio.",
                   " That unit has no registered emails: let them know some other way.");
      } else {
        texto += L(" No se pudo enviar el correo de aviso a la casa.",
                   " The notification email to the unit could not be sent.");
      }
      setAviso({ tipo: casaAviso.enviado ? "ok" : "alerta", texto });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      if (e.data?.errores) setErrores(e.data.errores);
      setAviso({ tipo: "error", texto: e.message });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setGuardando(false);
    }
  }

  // La persona ya está en la entrada: se le registra el ingreso de una vez,
  // con el formulario lleno.
  const irARegistrar = (state) => navigate("/garita/registrar", { state });

  function registrarAutorizada(a) {
    irARegistrar({
      casa: a.filial_id,
      autorizacion: a.id,
      autorizado_por: a.autorizado_por || "",
      identificacion: a.identificacion || "",
      nombre: a.nombre,
      placa: a.placa || "",
      motivo: a.motivo,
    });
  }

  // Alguien de la casa entra a la suya: no lleva autorización ninguna.
  function registrarResidente(s) {
    irARegistrar({
      casa: s.filial_id,
      categoria: s.categoria,
      persona: s.persona_id,
      identificacion: s.identificacion,
      nombre: s.nombre,
      placa: s.placa || "",
    });
  }

  function otra() {
    setForm(VACIO);
    setSugerencia(null);
    setListo(null);
    setAviso(null);
  }

  // Alguien de la casa no necesita autorización, y una visita que ya está
  // autorizada para hoy no se autoriza dos veces (sería otro correo).
  const yaResuelta = sugerencia?.origen === "residente" || sugerencia?.origen === "temporal";

  return (
    <>
      <h1 className="gar-titulo">{L("Autorizar visita de hoy", "Authorize a visit for today")}</h1>

      {aviso && (
        <div className={`gar-aviso ${aviso.tipo}`} style={{ marginBottom: 12 }}>
          <i className={`fa-solid ${aviso.tipo === "ok" ? "fa-circle-check" : "fa-triangle-exclamation"}`} />
          <span>{aviso.texto}</span>
        </div>
      )}

      {listo ? (
        <div className="gar-card gar-stack">
          <div>
            <strong style={{ fontSize: 17 }}>{listo.nombre}</strong>
            <div className="gar-fila" style={{ gap: 6, marginTop: 6 }}>
              <span className="gar-tag marca">{listo.filial}</span>
              <span className="gar-tag">{textoMotivo(listo.motivo)}</span>
              {listo.identificacion && <small>{listo.identificacion}</small>}
              {listo.placa && <span className="gar-placa">{listo.placa}</span>}
            </div>
            <div style={{ color: "var(--gar-suave)", fontSize: 14, marginTop: 10 }}>
              {L("Ya está en \"Autorizados de hoy\". Cuando llegue, registrale el ingreso desde ahí.",
                 "It is already in \"Authorized for today\". When they arrive, register the entry from there.")}
            </div>
          </div>
          <button type="button" className="gar-btn gar-btn-primario gar-btn-grande gar-btn-bloque"
            onClick={() => registrarAutorizada(listo)}>
            <i className="fa-solid fa-right-to-bracket" />
            {L("Ya llegó: registrar ingreso", "Already here: register entry")}
          </button>
          <div className="gar-fila" style={{ gap: 8 }}>
            <button type="button" className="gar-btn gar-btn-sec" style={{ flex: 1 }} onClick={otra}>
              <i className="fa-solid fa-circle-plus" /> {L("Autorizar otra", "Authorize another")}
            </button>
            <button type="button" className="gar-btn gar-btn-sec" style={{ flex: 1 }} onClick={() => navigate("/garita")}>
              <i className="fa-solid fa-list-check" /> {L("Ver la lista", "See the list")}
            </button>
          </div>
        </div>
      ) : (
        <div className="gar-stack">
          <div className="gar-aviso info">
            <i className="fa-solid fa-phone" />
            <span>
              {L("Para cuando un residente llama a autorizar una visita. Al guardar se le avisa por correo a la casa; el ingreso se registra después, cuando la persona llegue.",
                 "For when a resident calls to authorize a visit. On save the unit is notified by email; the entry is registered later, when the person arrives.")}
            </span>
          </div>

          <div className="gar-card gar-stack">
            <div className="gar-grid-2">
              <div>
                <label className="gar-label" htmlFor="gar-aut-ident">{L("Identificación", "ID")}</label>
                <div className="gar-fila" style={{ flexWrap: "nowrap" }}>
                  <input id="gar-aut-ident" className="gar-input" inputMode="text" autoComplete="off"
                    placeholder={L("Cédula (opcional)", "ID (optional)")}
                    value={form.identificacion}
                    onChange={(e) => { set("identificacion", e.target.value); setSugerencia(null); }}
                    onBlur={buscar} />
                  <button type="button" className="gar-icono-btn" style={{ width: 48, height: 48 }}
                    onClick={buscar} aria-label={L("Buscar cédula", "Look up ID")}>
                    <i className={`fa-solid ${buscando ? "fa-circle-notch fa-spin" : "fa-magnifying-glass"}`} />
                  </button>
                </div>
              </div>
              <div>
                <label className="gar-label" htmlFor="gar-aut-nombre">{L("Nombre", "Name")} *</label>
                <input id="gar-aut-nombre" className={`gar-input ${errores.nombre ? "error" : ""}`}
                  autoComplete="off" placeholder={L("Nombre y apellidos", "Full name")}
                  value={form.nombre} onChange={(e) => set("nombre", e.target.value)} />
                {errores.nombre && <div className="gar-error">{errores.nombre}</div>}
              </div>
            </div>

            {sugerencia?.origen === "externo" && (
              <div className="gar-aviso info">
                <i className="fa-solid fa-id-card" />
                <span>
                  {L("El nombre lo trajo el sistema por la cédula. No está registrado en el condominio.",
                     "The name was looked up from the ID. Not registered in this condominium.")}
                </span>
              </div>
            )}
            {sugerencia?.origen === "residente" && (
              <div className="gar-aviso ok">
                <i className="fa-solid fa-user-check" />
                <span>
                  <strong>{textoDe(sugerencia.categoria)} {L("de", "of")} {sugerencia.filial}</strong>.{" "}
                  {L("No necesita autorización: registrale el ingreso directo.",
                     "No authorization needed: just register the entry.")}
                </span>
              </div>
            )}
            {sugerencia?.origen === "temporal" && (
              <div className="gar-aviso ok">
                <i className="fa-solid fa-calendar-check" />
                <span>
                  <strong>{L("Ya está autorizado para hoy", "Already authorized for today")}</strong>
                  {sugerencia.autorizado_por ? L(`, lo autorizó ${sugerencia.autorizado_por}`,
                                                 `, authorized by ${sugerencia.autorizado_por}`) : ""}.{" "}
                  {L("No hace falta autorizarlo otra vez.", "No need to authorize it again.")}
                </span>
              </div>
            )}
            {yaResuelta && (
              <button type="button" className="gar-btn gar-btn-primario gar-btn-bloque"
                onClick={() => (sugerencia.origen === "temporal"
                  ? registrarAutorizada(sugerencia)
                  : registrarResidente(sugerencia))}>
                <i className="fa-solid fa-right-to-bracket" /> {L("Registrar su ingreso", "Register the entry")}
              </button>
            )}

            <div>
              <label className="gar-label" htmlFor="gar-aut-placa">{L("Placa", "Plate")}</label>
              <input id="gar-aut-placa" className="gar-input" autoComplete="off"
                placeholder={L("Opcional", "Optional")} style={{ fontWeight: 800 }}
                value={form.placa} onChange={(e) => set("placa", e.target.value.toUpperCase())} />
            </div>

            <div>
              <label className="gar-label">{L("Casa que la autoriza", "Unit authorizing it")} *</label>
              <CasaPicker casas={casas} value={form.filial} error={errores.filial}
                onChange={(id) => set("filial", id)} />
              {casa && casa.correos_aviso === 0 && (
                <div className="gar-aviso alerta" style={{ marginTop: 8 }}>
                  <i className="fa-solid fa-triangle-exclamation" />
                  <span>
                    {L(`${casa.idFilial} no tiene correos registrados: no se le va a poder avisar.`,
                       `${casa.idFilial} has no registered emails: it will not be possible to notify them.`)}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="gar-label">{L("Motivo de la visita", "Reason for visit")}</label>
              <div className="gar-chips">
                {MOTIVOS.map((m) => (
                  <button type="button" key={m.value}
                    className={`gar-chip ${form.motivo === m.value ? "activo" : ""}`}
                    onClick={() => set("motivo", m.value)}>
                    <i className={`fa-solid ${m.icon}`} /> {L(m.es, m.en)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="gar-label" htmlFor="gar-aut-notas">{L("Notas", "Notes")}</label>
              <textarea id="gar-aut-notas" className="gar-textarea"
                placeholder={L("Quién llamó, a qué viene…", "Who called, what for…")}
                value={form.notas} onChange={(e) => set("notas", e.target.value)} />
            </div>
          </div>

          <button type="button" className="gar-btn gar-btn-primario gar-btn-grande gar-btn-bloque"
            disabled={guardando} onClick={guardar}>
            <i className={`fa-solid ${guardando ? "fa-circle-notch fa-spin" : "fa-user-shield"}`} />
            {guardando ? L("Autorizando…", "Authorizing…") : L("Autorizar para hoy y avisar a la casa",
                                                               "Authorize for today and notify the unit")}
          </button>
        </div>
      )}
    </>
  );
}
